<template>
  <div>
    <dx-modal
      id="package-modal"
      title="Lista de pacotes"
      size="lg"
      :value="true"
      @input="close"
    >
      <div class="columns">
        <div class="column form-group">
          <div class="input-group">
            <input
              type="text"
              class="form-input"
              v-model="filterSearch"
              @keydown.enter="find"
              placeholder="Filtrar pacotes por nome"
            >
            <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                    @click="find">
              <fa-icon :icon="['fal', 'search']" />
            </button>
          </div>
        </div>
        <div class="column col-auto">
          <button class="btn btn-primary btn-icon btn-icon-left"
                  :disabled="managementModal.loading"
                  @click="openManagementModal(null)">
            <fa-icon :icon="['fal', 'plus']"/>Novo pacote
          </button>
        </div>
      </div>
      <div class="loading loading-lg" v-if="loading" />
      <template v-else>
        <template v-if="data.items.length === 0">
          <div class="empty mt-2">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Pacotes</p>
            <p class="empty-subtitle">
              Nenhum pacote encontrado. Verifique os filtros para realizar novas buscas.
            </p>
          </div>
        </template>
        <template v-else>
          <table class="table table-striped table-hover">
            <thead>
            <tr>
              <th class="hide-sm" style="width: 40px">#</th>
              <th>Nome</th>
              <th class="text-left">Tipo</th>
              <th class="hide-sm hide-md text-right">Ativo</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in data.items" :key="i">
              <td class="hide-sm">{{ i + 1 }}</td>
              <td>
                <span class="hover-link"
                      :class="{loading: managementModal.loading}"
                      @click="openManagementModal(item.id)">
                  {{ item.name }}
                </span>
              </td>
              <td class="text-left">{{ getTypeName(item.type) }}</td>
              <td class="hide-sm hide-md text-right">{{ item.active ? 'Sim' : 'Não' }}</td>
              <td class="text-right">
                <button class="btn btn-sm btn-action btn-icon btn-secondary"
                        :class="{loading: managementModal.loading}"
                        :disabled="deleting || managementModal.loading"
                        @click="openManagementModal(item.id)">
                  <fa-icon :icon="['fal', 'pencil']"></fa-icon>
                </button>
                <button class="btn btn-sm btn-action btn-icon btn-error ml-1"
                        :class="{loading: deleting}"
                        :disabled="deleting || managementModal.loading"
                        @click="remove(item.id)">
                  <fa-icon :icon="['fal', 'times']"></fa-icon>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
        <div class="text-center" v-if="data.hasMore">
          <button
            class="btn btn-primary btn-fixed-width"
            @click="loadMore"
            :disabled="loadingMore"
            :class="{ loading: loadingMore }"
          >Carregar mais</button>
        </div>
      </template>
      <template v-slot:footer>
        <button class="btn" @click="close">Sair</button>
      </template>
    </dx-modal>

    <dx-modal
      id="management-modal"
      title="Gerenciamento de pacote"
      size="lg"
      v-model="managementModal.show"
    >
      <div class="loading loading-lg" v-if="managementModal.loading" />
      <template v-else>
          <div class="columns">
            <div class="column col-8 col-md-8 col-sm-12 form-group"
                 :class="{'has-error': $v.managementModal.form.name.$error}">
              <label for="name" class="form-label">Nome</label>
              <input type="text" v-focus
                     class="form-input"
                     v-model="managementModal.form.name"
                     @blur="$v.managementModal.form.name.$touch()"
                     placeholder="Informe o nome do pacote"
              >
            </div>
            <div class="column col-4 col-md-4 col-sm-12 form-group">
              <label for="type" class="form-label">Tipo</label>
              <select class="form-select"
                      v-model="managementModal.form.type">
                <option v-for="(text, value) in places"
                        :value="value" :key="value">{{ text }}</option>
              </select>
            </div>
          </div>
          <h6 class="text-secondary mt-2">Serviços, taxas e outras despesas</h6>
          <div class="divider" />
          <div class="form-group"
               :class="{'has-error': $v.managementModal.form.expenses.$error}">
            <dx-autocomplete
              v-model="managementModal.expense"
              :source="findExpense"
              label="name"
              track-by="id"
              :debounce="800"
              @select="addExpense"
              placeholder="Informe o código ou nome do serviço..."
              input-id="expense-name"
            >
              <template v-slot="{ item }">
                <a>
                  <fa-icon :icon="['fal', 'file-medical-alt']" class="text-primary"/>
                  <span class="ml-1">
                   {{ item.name }}
                  <span v-if="item.tuss?.code">({{ item.tuss.code | tuss }})</span>
                </span>
                </a>
              </template>
              <template v-slot:action>
                <button
                  type="button"
                  class="btn btn-action input-group-btn btn-icon btn-neutral"
                  @click="findExpenseModalShow = true"
                >
                  <fa-icon :icon="['fal', 'search']" />
                </button>
              </template>
            </dx-autocomplete>
          </div>
          <div class="empty mt-2" v-if="managementModal.form.expenses.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Serviços, taxas e outras despesas</p>
            <p class="empty-subtitle">
              Nenhum serviço adicionado. Realize a busca utilizando o campo acima.
            </p>
          </div>
          <table class="table" v-else>
            <tr>
              <th>#</th>
              <th>Código</th>
              <th>Nome</th>
              <th>Tipo</th>
              <th class="text-center" style="width: 100px">Quantidade</th>
              <th style="width: 50px"></th>
            </tr>
            <tr v-for="(item, i) in managementModal.form.expenses" :key="i">
              <td>{{ i + 1 }}</td>
              <td>{{ item.code | tuss }}</td>
              <td>{{ item.name }}</td>
              <td>{{ getExpenseName(item.type) }}</td>
              <td class="text-center">
                <dx-input-number
                  class="form-input text-center"
                  v-model="item.quantity"
                  @blur="setQuantity(item)"
                  maxlength="4"
                />
              </td>
              <td class="text-right">
                <button class="btn btn-sm btn-action btn-icon btn-error"
                        @click="removeExpense(item.id)">
                  <fa-icon :icon="['fal', 'times']"></fa-icon>
                </button>
              </td>
            </tr>
          </table>
        </template>
      <template v-slot:footer>
        <button class="btn btn-icon btn-icon-left btn-primary mr-1"
                v-if="!managementModal.loading"
                :class="{loading: managementModal.saving}"
                :disabled="managementModal.saving"
                @click="save">
          <fa-icon :icon="['fal', 'save']" />Salvar
        </button>
        <button class="btn" @click="managementModal.show = false">Sair</button>
      </template>
    </dx-modal>

    <find-expense-modal
      v-if="findExpenseModalShow"
      @selected="findExpenseModalSelected"
      @close="findExpenseModalShow = false"
    />
  </div>
</template>

<script>
import { getName as getExpenseName } from '@/data/expense-types';
import { places, getName as getTypeName } from '@/data/place-types';
import { DELETE_EXPENSE } from '@/data/actions/modules/general';
import { required, minLength } from 'vuelidate/lib/validators';
import FindExpenseModal from '@/app/expense/components/modals/FindExpenseModal.vue';
import formMixin from '@/mixins/form';
import { mergeFrom } from '@/helpers/object';

export default {
  mixins: [formMixin],
  components: { FindExpenseModal },
  data() {
    return {
      loading: false,
      loadingMore: false,
      deleting: false,
      places,
      getTypeName,
      getExpenseName,
      filterSearch: '',
      findExpenseModalShow: false,
      data: {
        items: [],
        hasMore: false,
        limit: 50,
        offset: 0,
      },
      managementModal: {
        show: false,
        loading: false,
        saving: false,
        expense: null,
        form: this.blankForm(),
      },
    };
  },
  mounted() {
    this.load();
  },
  computed: {
    canRemoveAccess() {
      return this.$can(DELETE_EXPENSE);
    },
  },
  validations() {
    return {
      managementModal: {
        form: {
          name: { required },
          type: { required },
          active: { required },
          expenses: {
            required,
            minLength: minLength(1),
          },
        },
      },
    };
  },
  methods: {
    find() {
      this.data.offset = 0;
      this.load();
    },
    async loadMore() {
      this.data.offset += this.data.limit;
      await this.load();
    },
    load() {
      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
      };

      if (this.filterSearch.trim().length > 0) {
        params.search = this.filterSearch.trim();
      }

      return this.$http.get('/packages', { params })
        .then(({ data }) => {
          this.data.hasMore = data.hasMore;
          this.data.items = firstData ? data.items : [...this.data.items, ...data.items];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    findPackage(id) {
      return this.$http.get(`/packages/${id}`)
        .then(({ data }) => data)
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        });
    },
    create() {
      // this.$router.push('/clinical/packages/create');
    },
    edit() {
      // this.$router.push(`/clinical/packages/${item.id}/edit`);
    },
    close() {
      this.$emit('close');
    },
    remove(id) {
      if (!this.canRemoveAccess) {
        this.$toast.error('Acesso não autorizado!');
        return;
      }

      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente excluir este pacote?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.deleting = true;
              close();
              return this.$http.delete(`/packages/${id}`)
                .then(() => {
                  const found = this.data.items.find(item => item.id === id);
                  if (found) {
                    const idx = this.data.items.indexOf(found);
                    this.data.items.splice(idx, 1);
                  }
                  this.$toast.show('Registro excluído com sucesso!');
                })
                .catch(() => {
                  this.$toast.error('Ocorreu um erro. Tente novamente!');
                })
                .finally(() => {
                  this.deleting = false;
                });
            },
          },
        ],
      });
    },
    async openManagementModal(id) {
      if (this.managementModal.loading) {
        return;
      }

      this.managementModal.loading = true;
      this.managementModal.form = id
        ? mergeFrom(this.blankForm(), await this.findPackage(id))
        : this.blankForm();
      this.managementModal.show = true;
      this.managementModal.loading = false;
    },
    findExpense(search) {
      const params = {
        search,
        limit: 30,
        offset: 0,
        active: true,
      };

      return this.$httpX.get('/expenses', { params })
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [{
              id: null,
              type: null,
              code: null,
              name: 'Serviço não encontrado!',
              quantity: 0,
            }];
          }
          return data.items;
        });
    },
    addExpense(expense) {
      if (!expense || expense.id === null) {
        return;
      }

      const found = this.managementModal.form.expenses
        .find(({ id }) => id === expense.id);

      if (found) {
        found.quantity += 1;
      } else {
        this.managementModal.form.expenses.push({
          id: expense.id,
          type: expense.type,
          code: expense?.tuss?.code || null,
          name: expense.name,
          quantity: 1,
        });
      }

      this.managementModal.expense = null;
    },
    removeExpense(id) {
      const i = this.managementModal.form.expenses.findIndex(item => item.id === id);
      this.managementModal.form.expenses.splice(i, 1);
    },
    findExpenseModalSelected(items) {
      if (!items) {
        return;
      }

      items.forEach((item) => {
        const found = this.managementModal.form.expenses
          .find(({ id }) => id === item.id);

        if (found) {
          found.quantity += 1;
        } else {
          this.managementModal.form.expenses.push({
            id: item.id,
            type: item.type,
            code: item?.tuss?.code || null,
            name: item.name,
            quantity: 1,
          });
        }
      });

      this.managementModal.expense = null;
      this.findExpenseModalShow = false;
    },
    async save() {
      this.$v.managementModal.form.$touch();
      if (this.$v.managementModal.form.$error) {
        return;
      }

      this.managementModal.saving = true;

      const packageData = this.clone(this.managementModal.form);

      const request = this.managementModal.form.id
        ? this.$http.put(`/packages/${this.managementModal.form.id}`, packageData)
        : this.$http.post('/packages', packageData);

      // eslint-disable-next-line consistent-return
      return request
        .then(() => {
          this.$toast.show('Registro salvo!');
          this.managementModal.show = false;
          this.load();
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        }).finally(() => {
          this.managementModal.saving = false;
        });
    },
    setQuantity(expense) {
      if (expense.quantity === 0) {
        expense.quantity = 1;
      }
    },
    blankForm() {
      return {
        id: null,
        type: 'clinical',
        name: '',
        active: true,
        expenses: [],
      };
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';

#package-modal {
  .btn-fixed-width {
    font-size: $font-size;
    height: 1.8rem;
    margin: $layout-spacing-lg 0;
    width: 8rem;
  }
}
</style>
